import React, { useState, useEffect, useCallback } from 'react';
import styles from './CartContent.module.scss';
import { useSharedCart } from '../../entrypoints/theme';
import CartItem from '../CartItem/CartItem';
import { cartStore } from '../../hooks/cartStore';

interface CartContentProps {
  type: string;
}

interface CartItem {
  key: string;
  id: number;
  handle: string;
  properties?: {
    _isFreeGift?: boolean;
    is_custom_option?: boolean;
  };
}

interface Product {
  variants: Array<{
    id: number;
  }>;
}

interface CartState {
  count: number;
  items: CartItem[];
  main_items_total?: string;
  gift_threshold_1?: string;
  gift_threshold_2?: string;
  gift_threshold_3?: string;
}

interface CartState {
  count: number;
  items: CartItem[];
  main_items_total?: string;
  gift_threshold_1?: string;
  gift_threshold_2?: string;
  gift_threshold_3?: string;
}

interface SharedCartHook {
  cartState: CartState;
}

export default function CartContent({ type }: CartContentProps) {
  const { cartState } = useSharedCart() as SharedCartHook;
  const tierOneCollectionHandle: string = "tier-1-collection";
  const tierTwoCollectionHandle: string = "tier-2-collection";
  const tierThreeCollectionHandle: string = "tier-3-collection";

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tierOneProducts, setTierOneProducts] = useState<Product[]>([]);
  const [tierTwoProducts, setTierTwoProducts] = useState<Product[]>([]);
  const [tierThreeProducts, setTierThreeProducts] = useState<Product[]>([]);
  const [isLoadingTierProducts, setIsLoadingTierProducts] =
    useState<boolean>(true);
  const [
    popupShownForCurrentQualification,
    setPopupShownForCurrentQualification,
  ] = useState<boolean>(false);

  const tierOneMin: number = parseFloat(cartState?.gift_threshold_1 || "0");
  const tierTwoMin: number = parseFloat(cartState?.gift_threshold_2 || "0");
  const tierThreeMin: number = parseFloat(cartState?.gift_threshold_3 || "0");

  useEffect(() => {
    setIsLoading(true);
    const fetchProducts = async (handle: string): Promise<Product[]> => {
      if (!handle) return [];
      try {
        const response = await fetch(`/collections/${handle}/products.json`);
        if (!response.ok) {
          console.error(
            `Error fetching collection ${handle}: ${response.statusText}`
          );
          return [];
        }
        const data = await response.json();
        setIsLoading(false);
        return data.products || [];
      } catch (error) {
        console.error(
          `Error fetching products for collection ${handle}:`,
          error
        );
        return [];
      }
    };

    const fetchAllTiers = async (): Promise<void> => {
      setIsLoadingTierProducts(true);
      try {
        const [tierOne, tierTwo, tierThree] = await Promise.all([
          fetchProducts(tierOneCollectionHandle),
          fetchProducts(tierTwoCollectionHandle),
          fetchProducts(tierThreeCollectionHandle),
        ]);

        setTierOneProducts(tierOne);
        setTierTwoProducts(tierTwo);
        setTierThreeProducts(tierThree);
      } catch (error) {
        console.error("Error fetching tier products:", error);
        setTierOneProducts([]);
        setTierTwoProducts([]);
        setTierThreeProducts([]);
      } finally {
        setIsLoadingTierProducts(false);
      }
    };

    fetchAllTiers();
  }, [
    tierOneCollectionHandle,
    tierTwoCollectionHandle,
    tierThreeCollectionHandle,
  ]);
  
  const isValidTierForItem = useCallback(
    (item: CartItem) => {
      const totalPriceDollars: number = parseFloat(
        cartState?.main_items_total ?? "0"
      );
      const isTierOneUnlocked: boolean =
        tierOneMin > 0 && totalPriceDollars >= tierOneMin;
      const isTierTwoUnlocked: boolean =
        tierTwoMin > 0 && totalPriceDollars >= tierTwoMin;
      const isTierThreeUnlocked: boolean =
        tierThreeMin > 0 && totalPriceDollars >= tierThreeMin;

      const isItemInProductList = (
        products: Product[],
        currentItem: CartItem
      ): boolean =>
        products.some((product) =>
          product.variants.some((variant) => variant.id === currentItem.id)
        );

      const isInTierOne: boolean =
        tierOneProducts.length > 0 &&
        isItemInProductList(tierOneProducts, item);
      const isInTierTwo: boolean =
        tierTwoProducts.length > 0 &&
        isItemInProductList(tierTwoProducts, item);
      const isInTierThree: boolean =
        tierThreeProducts.length > 0 &&
        isItemInProductList(tierThreeProducts, item);

      if (isInTierThree && isTierThreeUnlocked) return true;
      if (isInTierTwo && isTierTwoUnlocked) return true;
      if (isInTierOne && isTierOneUnlocked) return true;

      return false;
    },
    [
      cartState?.main_items_total,
      tierOneMin,
      tierTwoMin,
      tierThreeMin,
      tierOneProducts,
      tierTwoProducts,
      tierThreeProducts,
    ]
  );

  useEffect(() => {
    if (
      isLoadingTierProducts ||
      !cartState ||
      !Array.isArray(cartState.items)
    ) {
      return;
    }

    const itemsToRemove: string[] = [];
    let firstValidFreeGiftKey: string | null = null;
    let hasAnyFreeGiftProperty: boolean = false;

    cartState.items.forEach((item) => {
      const isFreeGift: boolean = !!item.properties?._isFreeGift;
      const isNavidiumItem: boolean = item.handle.includes("navidium");

      if (isFreeGift) {
        hasAnyFreeGiftProperty = true;
        const isItemValid: boolean = isValidTierForItem(item);
        if (isItemValid) {
          if (firstValidFreeGiftKey === null) {
            firstValidFreeGiftKey = item.key;
          } else {
            itemsToRemove.push(item.key);
          }
        } else {
          itemsToRemove.push(item.key);
        }
      } else if (isNavidiumItem) {
        itemsToRemove.push(item.key);
      }
    });

    let didRemoveItems: boolean = false;
    if (itemsToRemove.length > 0) {
      const currentItemKeys: Set<string> = new Set(
        cartState.items.map((i) => i.key)
      );
      const keysActuallyToRemove: string[] = itemsToRemove.filter((key) =>
        currentItemKeys.has(key)
      );

      if (keysActuallyToRemove.length > 0) {
        didRemoveItems = true;
        keysActuallyToRemove.forEach((key) => {
          cartStore.removeItem(key);
        });
      }
    }

    const totalPriceDollars: number = parseFloat(
      cartState?.main_items_total ?? "0"
    );
    const isTierOneUnlocked: boolean =
      tierOneMin > 0 && totalPriceDollars >= tierOneMin;
    const isTierTwoUnlocked: boolean =
      tierTwoMin > 0 && totalPriceDollars >= tierTwoMin;
    const isTierThreeUnlocked: boolean =
      tierThreeMin > 0 && totalPriceDollars >= tierThreeMin;
    const isAnyTierUnlocked: boolean =
      isTierOneUnlocked || isTierTwoUnlocked || isTierThreeUnlocked;

    const shouldShowPopup: boolean =
      isAnyTierUnlocked &&
      !hasAnyFreeGiftProperty &&
      !didRemoveItems &&
      window.location.pathname.startsWith("/cart");

    if (shouldShowPopup && !popupShownForCurrentQualification) {
      cartStore.openGiftPopup();
      setPopupShownForCurrentQualification(true);
    } else if (!shouldShowPopup && popupShownForCurrentQualification) {
      setPopupShownForCurrentQualification(false);
    }
  }, [
    cartState,
    isLoadingTierProducts,
    isValidTierForItem,
    tierOneMin,
    tierTwoMin,
    tierThreeMin,
    cartStore,
    popupShownForCurrentQualification,
  ]);

  useEffect(() => {
    if (
      cartState &&
      cartState.count === 0 &&
      window.location.pathname.startsWith("/cart")
    ) {
      if (typeof window !== "undefined" && typeof document !== "undefined") {
        const urlElement: HTMLInputElement | null = document.getElementById(
          "empty_cart_url_value"
        ) as HTMLInputElement;
        const emptyCartUrl: string | undefined = urlElement?.value;

        if (emptyCartUrl) {
          window.location.href = emptyCartUrl;
        } else {
          console.warn(
            "Element with ID 'empty_cart_url_value' not found or has no value. Cannot redirect."
          );
        }
      }
    }
  }, [cartState]);

  const renderEmptyCart = () => (
    <div className={styles.emptyCart}>
      <svg width="135" height="74" viewBox="0 0 135 74" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="67.5" cy="68" rx="67.5" ry="6" fill="black" fillOpacity="0.05"></ellipse>
        <g filter="url(#filter0_f_1144_107832)">
          <ellipse cx="24" cy="68" rx="7" ry="1.5" fill="black" fillOpacity="0.1"></ellipse>
        </g>
        <g filter="url(#filter1_f_1144_107832)">
          <ellipse cx="115" cy="68" rx="7" ry="1.5" fill="black" fillOpacity="0.1"></ellipse>
        </g>
        <g clipPath="url(#clip0_1144_107832)">
          <path d="M93.1596 1.10296L14.7988 22.0997C13.3818 22.4793 12.1737 23.4064 11.4402 24.6768C10.7067 25.9472 10.508 27.457 10.8876 28.874L28.0668 92.9874C28.4464 94.4043 29.3735 95.6125 30.6439 96.346C31.9144 97.0795 33.4241 97.2782 34.8411 96.8985L113.202 75.9018C114.619 75.5222 115.827 74.5951 116.56 73.3247C117.294 72.0542 117.493 70.5445 117.113 69.1275L99.9339 5.01415C99.5543 3.59715 98.6272 2.38903 97.3568 1.65554C96.0863 0.922047 94.5766 0.72328 93.1596 1.10296ZM113.551 70.0819C113.678 70.5542 113.612 71.0575 113.367 71.4809C113.123 71.9044 112.72 72.2134 112.247 72.34L33.8867 93.3367C33.4144 93.4632 32.9111 93.397 32.4877 93.1525C32.0642 92.908 31.7552 92.5053 31.6286 92.033L14.4495 27.9196C14.3229 27.4473 14.3892 26.944 14.6337 26.5206C14.8782 26.0971 15.2809 25.7881 15.7532 25.6615L94.114 4.66481C94.5863 4.53825 95.0896 4.60451 95.513 4.84901C95.9365 5.0935 96.2455 5.49621 96.3721 5.96854L113.551 70.0819ZM78.8186 25.9423C80.2107 31.138 79.4819 36.6738 76.7925 41.3321C74.103 45.9904 69.6732 49.3895 64.4775 50.7817C59.2819 52.1738 53.746 51.445 49.0877 48.7556C44.4295 46.0661 41.0304 41.6363 39.6382 36.4407C39.5116 35.9683 39.5779 35.4651 39.8224 35.0416C40.0669 34.6181 40.4696 34.3091 40.9419 34.1825C41.4142 34.056 41.9175 34.1222 42.341 34.3667C42.7645 34.6112 43.0735 35.0139 43.2 35.4863C44.3391 39.7372 47.1202 43.3616 50.9315 45.5621C54.7428 47.7626 59.2722 48.3589 63.5231 47.2198C67.7741 46.0808 71.3985 43.2997 73.599 39.4884C75.7994 35.677 76.3957 31.1477 75.2567 26.8967C75.1301 26.4244 75.1964 25.9211 75.4409 25.4976C75.6854 25.0742 76.0881 24.7652 76.5604 24.6386C77.0328 24.512 77.536 24.5783 77.9595 24.8228C78.383 25.0673 78.692 25.47 78.8186 25.9423Z" fill="black"></path>
        </g>
        <defs>
          <filter id="filter0_f_1144_107832" x="16" y="65.5" width="16" height="5" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"></feBlend>
            <feGaussianBlur stdDeviation="0.5" result="effect1_foregroundBlur_1144_107832"></feGaussianBlur>
          </filter>
          <filter id="filter1_f_1144_107832" x="107" y="65.5" width="16" height="5" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"></feBlend>
            <feGaussianBlur stdDeviation="0.5" result="effect1_foregroundBlur_1144_107832"></feGaussianBlur>
          </filter>
          <clipPath id="clip0_1144_107832">
            <rect width="110" height="68" fill="white" transform="translate(9)"></rect>
          </clipPath>
        </defs>
      </svg>
      <p>Your cart is empty!</p>
    </div>
  );
  
  const renderLoading = () => (
    <div className={styles.loading}>
      <div className={styles.loadingSpinner}></div>
      <p>Loading...</p>
    </div>
  );

  const renderCartItems = () => {
    const itemsToDisplayInitially = cartState.items.filter(item => !item.properties?.is_custom_option);
    
    const freeGiftItems = itemsToDisplayInitially.filter(item => item.properties?._isFreeGift);
    const regularItems = itemsToDisplayInitially.filter(
      item => !item.properties?._isFreeGift && !item.handle.includes("navidium")
    );

    const sortedCartItems = [...freeGiftItems, ...regularItems];

    return sortedCartItems.map(item => <CartItem key={item.key} item={item} />);
  };

  return (
    <div className={`${styles.cartContent} ${styles[type]}`}>
      {cartState.count > 0 ? renderCartItems() : isLoading ? renderLoading() : renderEmptyCart()}
    </div>
  );
}

CartContent.displayName = "CartContent";
